import { deepLinkActivityFeedRouteHandler } from './deepLinkActivityFeedRouteHandler';
import { deepLinkAddCalendarRouteHandler } from './deepLinkAddCalendarRouteHandler';
import { deepLinkAddMembersGroupHandler } from './deepLinkAddMembersGroupHandler';
import { deepLinkCalendarWidgetRouteHandler } from './deepLinkCalendarWidgetRouteHandler';
import { deepLinkComposeRouteHandler } from './deepLinkComposeRouteHandler';
import { deepLinkCreateBookingsCalendarRouteHandler } from './deepLinkCreateBookingsCalendarRouteHandler';
import { deepLinkDataProviderRouteHandler } from './deepLinkDataProviderRouteHandler';
import { deepLinkDownloadMobileRouteHandler } from './deepLinkDownloadMobileRouteHandler';
import { deepLinkInboxRulesRouteHandler } from './deepLinkInboxRulesRouteHandler';
import { deepLinkInsightsRouteHandler } from './deepLinkInsightsRouteHandler';
import { deepLinkLocationCardRouteHandler } from './deepLinkLocationCardRouteHandler';
import { deepLinkOpenCreateGroupHandler } from './deepLinkOpenCreateGroupHandler';
import { deepLinkOpenDiscoverGroupHandler } from './deepLinkOpenDiscoverGroupHandler';
import { deepLinkOpenEditGroupHandler } from './deepLinkOpenEditGroupHandler';
import { deepLinkOrgExplorerRouteHandler } from './deepLinkOrgExplorerRouteHandler';
import { deepLinkPersonalSpaceRouteHandler } from './deepLinkPersonalSpaceRouteHandler';
import { deepLinkReadingPaneRouteHandler } from './deepLinkReadingPaneRouteHandler';
import { deepLinkRoomPanelRouteHandler } from './deepLinkRoomPanelRouteHandler';
import { deepLinkSharedSpaceConfigRouteHandler } from './deepLinkSharedSpaceConfigRouteHandler';
import { deepLinkSharedSpaceRouteHandler } from './deepLinkSharedSpaceRouteHandler';
import { deepLinkTimePanelRouteHandler } from './deepLinkTimePanelRouteHandler';
import { deepLinkTodoWidgetRouteHandler } from './deepLinkTodoWidgetRouteHandler';
import { deepLinkYammerRouteHandler } from './deepLinkYammerRouteHandler';
import { COMPOSE_ROUTE, READ_ROUTE } from '../store/schema/DeeplinkRoute';
import type { DeeplinkView } from '../store/schema/DeeplinkView';
import getStore from '../store/store';
import type { BootstrapOptions } from 'owa-bootstrap';
import { lazySetupCalendarDeeplinkFunctionalBoot } from 'owa-calendar-post-boot';
import { isHostAppFeatureEnabled } from 'owa-hostapp-feature-flags';
import loc from 'owa-localize';
import {
    calendarDeeplinkModuleNameText,
    roomPanelDeeplinkModuleNameText,
    timeStreamDeeplinkModuleNameText,
    timePanelDeeplinkModuleNameText,
    addCalendarDeeplinkModuleNameText,
    inboxRulesDeeplinkModuleNameText,
    locationCardDeeplinkModuleNameText,
    downloadMobileDeeplinkModuleNameText,
    activityFeedDeeplinkModuleNameText,
    meetingInsightsDeeplinkModuleNameText,
    orgExplorerDeeplinkModuleNameText,
    createGroupDeeplinkModuleNameText,
    editGroupDeeplinkModuleNameText,
    addMembersDeeplinkModuleNameText,
    discoverGroupsDeeplinkModuleNameText,
    yammerDeeplinkModuleNameText,
    createBookingsCalendarDeeplinkModuleNameText,
    calendarWidgetOpxModuleNameText,
    todoWidgetOpxModuleNameText,
} from '../strings.locstring.json';
import {
    lazySetupSharedOpxFunctionalBoot,
    lazySetupSharedOpxFunctionalBootWithNotifications,
} from 'owa-shared-opx-post-boot';
import {
    ADD_CALENDAR_ROUTE,
    MY_DAY_ROUTE,
    PERSONAL_SPACE_ROUTE,
    SHARED_SPACE_ROUTE,
    SHARED_SPACE_CONFIG_ROUTE,
    ROOM_PANEL_ROUTE,
    MEETING_INSIGHTS_ROUTE,
    LOCATION_CARD_ROUTE,
    CREATE_GROUP_ROUTE,
    ADD_MEMBERS_ROUTE,
    EDIT_GROUP_ROUTE,
    DISCOVER_GROUP_ROUTE,
    INBOX_RULES_ROUTE,
    DOWNLOAD_MOBILE_ROUTE,
    DATA_PROVIDER_ROUTE,
    ACTIVITY_FEED_ROUTE,
    ORG_EXPLORER_ROUTE,
    YAMMER_ROUTE,
    CREATE_BOOKINGS_CALENDAR_ROUTE,
    CALENDAR_WIDGET_ROUTE,
    TODO_WIDGET_ROUTE,
} from 'owa-opx-routes';

type DeeplinkDefinition = {
    getRouteRegistrations: () => string[];
    getRouteSegments: () => string[];
    routeHandler: (parameters: any) => void;
    postLazyAction: BootstrapOptions['postLazyAction'];
    getModuleName: BootstrapOptions['getModuleName'];
};

type DeeplinkDefinitions = { [view in DeeplinkView]: DeeplinkDefinition };

const LOC_CALENDAR_DEEPLINK_MODULE_TEXT = () => loc(calendarDeeplinkModuleNameText);

function getDeeplinkRouteRegistration(route) {
    const deepLinkRoutePrefix = getDeeplinkRoutePrefix();
    return `/${deepLinkRoutePrefix}/${route}`;
}

export function getDeeplinkRoutePrefix() {
    return isHostAppFeatureEnabled('opxDeeplink') ? 'opxdeeplink' : 'deeplink';
}

export const DEEPLINK_DEFINITIONS: DeeplinkDefinitions = {
    ['AddCalendar']: {
        getRouteRegistrations: () => [getDeeplinkRouteRegistration(ADD_CALENDAR_ROUTE)],
        getRouteSegments: () => [getDeeplinkRoutePrefix(), ADD_CALENDAR_ROUTE],
        routeHandler: deepLinkAddCalendarRouteHandler,
        postLazyAction: lazySetupCalendarDeeplinkFunctionalBoot,
        getModuleName: () => loc(addCalendarDeeplinkModuleNameText),
    },
    ['MyDay']: {
        getRouteRegistrations: () => [getDeeplinkRouteRegistration(MY_DAY_ROUTE)],
        getRouteSegments: () => [getDeeplinkRoutePrefix(), MY_DAY_ROUTE],
        routeHandler: deepLinkTimePanelRouteHandler,
        postLazyAction: lazySetupSharedOpxFunctionalBootWithNotifications,
        getModuleName: () => loc(timePanelDeeplinkModuleNameText),
    },
    ['PersonalSpace']: {
        getRouteRegistrations: () => [getDeeplinkRouteRegistration(PERSONAL_SPACE_ROUTE)],
        getRouteSegments: () => [getDeeplinkRoutePrefix(), PERSONAL_SPACE_ROUTE],
        routeHandler: deepLinkPersonalSpaceRouteHandler,
        postLazyAction: lazySetupSharedOpxFunctionalBootWithNotifications,
        getModuleName: () => loc(timeStreamDeeplinkModuleNameText),
    },
    ['SharedSpace']: {
        getRouteRegistrations: () => [getDeeplinkRouteRegistration(SHARED_SPACE_ROUTE)],
        getRouteSegments: () => [getDeeplinkRoutePrefix(), SHARED_SPACE_ROUTE],
        routeHandler: deepLinkSharedSpaceRouteHandler,
        postLazyAction: lazySetupSharedOpxFunctionalBootWithNotifications,
        getModuleName: () => loc(timeStreamDeeplinkModuleNameText),
    },
    ['SharedSpaceConfig']: {
        getRouteRegistrations: () => [getDeeplinkRouteRegistration(SHARED_SPACE_CONFIG_ROUTE)],
        getRouteSegments: () => [getDeeplinkRoutePrefix(), SHARED_SPACE_CONFIG_ROUTE],
        routeHandler: deepLinkSharedSpaceConfigRouteHandler,
        postLazyAction: lazySetupSharedOpxFunctionalBootWithNotifications,
        getModuleName: () => loc(timeStreamDeeplinkModuleNameText),
    },
    ['Read']: {
        getRouteRegistrations: () => [
            getDeeplinkRouteRegistration(`${READ_ROUTE}/:itemId`),
            getDeeplinkRouteRegistration(`${READ_ROUTE}/account/:account/:itemId`),
        ],
        getRouteSegments: () => {
            const { itemId, account } = getStore();
            return account
                ? [getDeeplinkRoutePrefix(), READ_ROUTE, 'account', account, itemId]
                : [getDeeplinkRoutePrefix(), READ_ROUTE, itemId];
        },
        routeHandler: deepLinkReadingPaneRouteHandler,
        postLazyAction: lazySetupCalendarDeeplinkFunctionalBoot,
        getModuleName: LOC_CALENDAR_DEEPLINK_MODULE_TEXT,
    },
    ['Compose']: {
        getRouteRegistrations: () => [
            getDeeplinkRouteRegistration(COMPOSE_ROUTE),
            getDeeplinkRouteRegistration(`${COMPOSE_ROUTE}/:itemId`),
            getDeeplinkRouteRegistration(`${COMPOSE_ROUTE}/account/:account/:itemId`),
        ],
        getRouteSegments: () => {
            const { itemId, account } = getStore();
            return account && itemId
                ? [getDeeplinkRoutePrefix(), COMPOSE_ROUTE, 'account', account, itemId]
                : itemId
                ? [getDeeplinkRoutePrefix(), COMPOSE_ROUTE, itemId]
                : [getDeeplinkRoutePrefix(), COMPOSE_ROUTE];
        },
        routeHandler: deepLinkComposeRouteHandler,
        postLazyAction: lazySetupCalendarDeeplinkFunctionalBoot,
        getModuleName: LOC_CALENDAR_DEEPLINK_MODULE_TEXT,
    },
    ['RoomPanel']: {
        getRouteRegistrations: () => [getDeeplinkRouteRegistration(ROOM_PANEL_ROUTE)],
        getRouteSegments: () => [getDeeplinkRoutePrefix(), ROOM_PANEL_ROUTE],
        routeHandler: deepLinkRoomPanelRouteHandler,
        postLazyAction: lazySetupSharedOpxFunctionalBoot,
        getModuleName: () => loc(roomPanelDeeplinkModuleNameText),
    },
    ['MeetingInsights']: {
        getRouteRegistrations: () => [getDeeplinkRouteRegistration(MEETING_INSIGHTS_ROUTE)],
        getRouteSegments: () => [getDeeplinkRoutePrefix(), MEETING_INSIGHTS_ROUTE],
        routeHandler: deepLinkInsightsRouteHandler,
        postLazyAction: lazySetupSharedOpxFunctionalBoot,
        getModuleName: () => loc(meetingInsightsDeeplinkModuleNameText),
    },
    ['LocationCard']: {
        getRouteRegistrations: () => [getDeeplinkRouteRegistration(LOCATION_CARD_ROUTE)],
        getRouteSegments: () => [getDeeplinkRoutePrefix(), LOCATION_CARD_ROUTE],
        routeHandler: deepLinkLocationCardRouteHandler,
        postLazyAction: lazySetupSharedOpxFunctionalBoot,
        getModuleName: () => loc(locationCardDeeplinkModuleNameText),
    },
    ['CreateGroup']: {
        getRouteRegistrations: () => [getDeeplinkRouteRegistration(CREATE_GROUP_ROUTE)],
        getRouteSegments: () => [getDeeplinkRoutePrefix(), CREATE_GROUP_ROUTE],
        routeHandler: deepLinkOpenCreateGroupHandler,
        postLazyAction: lazySetupSharedOpxFunctionalBoot,
        getModuleName: () => loc(createGroupDeeplinkModuleNameText),
    },
    ['AddMembers']: {
        getRouteRegistrations: () => [getDeeplinkRouteRegistration(ADD_MEMBERS_ROUTE)],
        getRouteSegments: () => [getDeeplinkRoutePrefix(), ADD_MEMBERS_ROUTE],
        routeHandler: deepLinkAddMembersGroupHandler,
        postLazyAction: lazySetupSharedOpxFunctionalBoot,
        getModuleName: () => loc(addMembersDeeplinkModuleNameText),
    },
    ['EditGroup']: {
        getRouteRegistrations: () => [getDeeplinkRouteRegistration(EDIT_GROUP_ROUTE)],
        getRouteSegments: () => [getDeeplinkRoutePrefix(), EDIT_GROUP_ROUTE],
        routeHandler: deepLinkOpenEditGroupHandler,
        postLazyAction: lazySetupSharedOpxFunctionalBoot,
        getModuleName: () => loc(editGroupDeeplinkModuleNameText),
    },
    ['DiscoverGroup']: {
        getRouteRegistrations: () => [getDeeplinkRouteRegistration(DISCOVER_GROUP_ROUTE)],
        getRouteSegments: () => [getDeeplinkRoutePrefix(), DISCOVER_GROUP_ROUTE],
        routeHandler: deepLinkOpenDiscoverGroupHandler,
        postLazyAction: lazySetupSharedOpxFunctionalBoot,
        getModuleName: () => loc(discoverGroupsDeeplinkModuleNameText),
    },
    ['InboxRules']: {
        getRouteRegistrations: () => [getDeeplinkRouteRegistration(INBOX_RULES_ROUTE)],
        getRouteSegments: () => [getDeeplinkRoutePrefix(), INBOX_RULES_ROUTE],
        routeHandler: deepLinkInboxRulesRouteHandler,
        postLazyAction: lazySetupSharedOpxFunctionalBootWithNotifications,
        getModuleName: () => loc(inboxRulesDeeplinkModuleNameText),
    },
    ['DownloadMobile']: {
        getRouteRegistrations: () => [getDeeplinkRouteRegistration(DOWNLOAD_MOBILE_ROUTE)],
        getRouteSegments: () => [getDeeplinkRoutePrefix(), DOWNLOAD_MOBILE_ROUTE],
        routeHandler: deepLinkDownloadMobileRouteHandler,
        postLazyAction: lazySetupSharedOpxFunctionalBoot,
        getModuleName: () => loc(downloadMobileDeeplinkModuleNameText),
    },
    ['DataProvider']: {
        getRouteRegistrations: () => [getDeeplinkRouteRegistration(DATA_PROVIDER_ROUTE)],
        getRouteSegments: () => [getDeeplinkRoutePrefix(), DATA_PROVIDER_ROUTE],
        routeHandler: deepLinkDataProviderRouteHandler,
        postLazyAction: lazySetupSharedOpxFunctionalBoot,
        getModuleName: () => '', // data-only OPX
    },
    ['ActivityFeed']: {
        getRouteRegistrations: () => [getDeeplinkRouteRegistration(ACTIVITY_FEED_ROUTE)],
        getRouteSegments: () => [getDeeplinkRoutePrefix(), ACTIVITY_FEED_ROUTE],
        routeHandler: deepLinkActivityFeedRouteHandler,
        postLazyAction: lazySetupSharedOpxFunctionalBootWithNotifications,
        getModuleName: () => loc(activityFeedDeeplinkModuleNameText),
    },
    ['OrgExplorer']: {
        getRouteRegistrations: () => [getDeeplinkRouteRegistration(ORG_EXPLORER_ROUTE)],
        getRouteSegments: () => [getDeeplinkRoutePrefix(), ORG_EXPLORER_ROUTE],
        routeHandler: deepLinkOrgExplorerRouteHandler,
        postLazyAction: lazySetupSharedOpxFunctionalBoot,
        getModuleName: () => loc(orgExplorerDeeplinkModuleNameText),
    },
    ['Yammer']: {
        getRouteRegistrations: () => [getDeeplinkRouteRegistration(YAMMER_ROUTE)],
        getRouteSegments: () => [getDeeplinkRoutePrefix(), YAMMER_ROUTE],
        routeHandler: deepLinkYammerRouteHandler,
        postLazyAction: lazySetupSharedOpxFunctionalBoot,
        getModuleName: () => loc(yammerDeeplinkModuleNameText),
    },
    ['CreateBookingsCalendar']: {
        getRouteRegistrations: () => [getDeeplinkRouteRegistration(CREATE_BOOKINGS_CALENDAR_ROUTE)],
        getRouteSegments: () => [getDeeplinkRoutePrefix(), CREATE_BOOKINGS_CALENDAR_ROUTE],
        routeHandler: deepLinkCreateBookingsCalendarRouteHandler,
        postLazyAction: lazySetupSharedOpxFunctionalBoot,
        getModuleName: () => loc(createBookingsCalendarDeeplinkModuleNameText),
    },
    ['CalendarWidget']: {
        getRouteRegistrations: () => [getDeeplinkRouteRegistration(CALENDAR_WIDGET_ROUTE)],
        getRouteSegments: () => [getDeeplinkRoutePrefix(), CALENDAR_WIDGET_ROUTE],
        routeHandler: deepLinkCalendarWidgetRouteHandler,
        postLazyAction: lazySetupSharedOpxFunctionalBootWithNotifications,
        getModuleName: () => loc(calendarWidgetOpxModuleNameText),
    },
    ['TodoWidget']: {
        getRouteRegistrations: () => [getDeeplinkRouteRegistration(TODO_WIDGET_ROUTE)],
        getRouteSegments: () => [getDeeplinkRoutePrefix(), TODO_WIDGET_ROUTE],
        routeHandler: deepLinkTodoWidgetRouteHandler,
        postLazyAction: lazySetupSharedOpxFunctionalBoot,
        getModuleName: () => loc(todoWidgetOpxModuleNameText),
    },
};
